import React, { useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";

import StarterTemplatesPaneController, {
    StarterTemplatesPaneControllerState,
} from "js/controllers/StarterTemplatesPaneController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { trackActivity } from "js/core/utilities/utilities";
import StarterTemplateList from "js/editor/CreatePresentation/starterTemplates/StarterTemplateList";
import getObjectHash from "common/utils/getObjectHash";

const TAG_FILTER_ALL = "__all__";

const TemplatesContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    flex: 1;
    gap: 20px;
`;

const ResultsContainer = styled.div`
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

export interface ChooseStarterTemplatePaneProps {
    tagFilter?: string;
    onCategoryClick?: (category: string) => void;
    onCreate: (options: { type: "starter-template"; templateId: string }) => void;
}

type ChooseStarterTemplatePaneComponentProps =
    WorkspaceControllerState &
    StarterTemplatesPaneControllerState &
    ChooseStarterTemplatePaneProps;

function ChooseStarterTemplatePaneInner({
    tagFilter: externalTagFilter,
    onCategoryClick,
    onCreate,
    initialized,
    filters
}: ChooseStarterTemplatePaneComponentProps) {
    const selectedTag = externalTagFilter ?? TAG_FILTER_ALL;

    const controllerFilters = (() => {
        if (selectedTag === TAG_FILTER_ALL) {
            return [];
        }

        return [{
            field: "resource.tags",
            operator: "eq" as const,
            value: selectedTag,
        }];
    })();

    useEffect(() => {
        StarterTemplatesPaneController.inititialize({
            sort: [{
                field: "resource.createdAt",
                direction: "desc" as const,
            }],
            filters: controllerFilters,
        });

        return () => {
            StarterTemplatesPaneController.reset();
        };
    }, []);

    useEffect(() => {
        if (!initialized) {
            return;
        }

        if (getObjectHash(controllerFilters) === getObjectHash(filters)) {
            return;
        }

        StarterTemplatesPaneController.setFilters(controllerFilters);
    }, [getObjectHash(controllerFilters), getObjectHash(filters), initialized]);

    const handleSelect = useCallback((templateId: string, isDoubleClick?: boolean) => {
        if (!isDoubleClick) {
            return;
        }

        onCreate({ type: "starter-template", templateId });
        trackActivity("Presentation", "Start", null, null, {
            source: "starter-template",
            template_id: templateId,
        }, { audit: false });
    }, [onCreate]);

    return (
        <TemplatesContainer>
            <ResultsContainer>
                <StarterTemplateList onSelect={handleSelect} onCategoryClick={onCategoryClick} />
            </ResultsContainer>
        </TemplatesContainer>
    );
}

export const ChooseStarterTemplatePane = WorkspaceController.withInitializedState(
    StarterTemplatesPaneController.withState(ChooseStarterTemplatePaneInner)
);
